import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { auth } from "@/lib/firebase";
import { PRICING } from "@/lib/pricing";
import "./LandingPage.css";

export function LandingPage() {
  const { user, signInWithGoogle } = useAuth();
  const { isPro } = useSubscription();
  const navigate = useNavigate();

  const handleGetStarted = async () => {
    if (user) {
      navigate("/add");
      return;
    }
    try {
      await signInWithGoogle();
    } catch {
      // Popup may have auto-closed
    }
    if (auth.currentUser) {
      navigate("/add");
    }
  };

  const handleStartPro = async () => {
    if (!user) {
      try {
        await signInWithGoogle();
      } catch {
        // Popup may have auto-closed
      }
      if (!auth.currentUser) return;
    }
    if (isPro) {
      navigate("/add?already_pro=1");
      return;
    }
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) return;
      const token = await currentUser.getIdToken();
      const response = await fetch("/api/stripe-checkout", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();
      if (!response.ok) {
        navigate("/add?already_pro=1");
        return;
      }
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.error("Failed to start checkout:", error);
    }
  };

  return (
    <div className="landing-v2">
      {/* NAV */}
      <nav className="lv2-nav">
        <div className="lv2-nav-inner">
          <a href="/" className="lv2-nav-logo">
            <img src="/logo.svg" alt="Shoresh" style={{ height: 28, width: 28 }} />
            <span>Shoresh</span>
          </a>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            {user ? (
              <button className="lv2-nav-cta" onClick={() => navigate("/add")}>
                Go to App
              </button>
            ) : (
              <>
                <button className="lv2-nav-login" onClick={signInWithGoogle}>Log in</button>
                <button className="lv2-nav-cta" onClick={handleGetStarted}>Try It Free</button>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section className="lv2-hero">
        <div className="lv2-hero-badge">For English-speaking olim in ulpan</div>
        <h1>
          You Moved to Israel. You're in Ulpan. You're Forgetting Faster Than You're <em>Learning.</em>
        </h1>
        <p className="lv2-hero-sub">
          You sit through 4 hours of class, write down 30 new words, and by Thursday you remember maybe 10.
        </p>
        <h2 className="lv2-hero-h1-style">
          Shoresh closes the gap — so the Hebrew you learn actually stays in your head.
        </h2>
        <button className="lv2-cta-btn" onClick={handleGetStarted}>
          Start My Word Bank — Free
        </button>
        <p className="lv2-cta-micro">Free plan. No credit card. Set up before your next class.</p>
      </section>

      {/* PHONE SHOWCASE */}
      <div className="lv2-phone-showcase">
        <FadeIn>
          <div className="lv2-phone-row">
            <div className="lv2-phone-frame" style={{ marginTop: 40 }}>
              <img src="/landing/hero-left.png" alt="Word Bank" />
            </div>
            <div className="lv2-phone-frame lv2-phone-pop">
              <img src="/landing/hero-center.png" alt="Translate" />
            </div>
            <div className="lv2-phone-frame" style={{ marginTop: 40 }}>
              <img src="/landing/hero-right.png" alt="Grammar" />
            </div>
          </div>
        </FadeIn>
      </div>

      {/* PAIN POINTS */}
      <div className="lv2-pain-section">
        <FadeIn>
          <div className="lv2-pain-inner">
            <div className="lv2-section-label" style={{ textAlign: "center" }}>BE HONEST</div>
            <div className="lv2-section-title" style={{ textAlign: "center" }}>This is your week, isn't it?</div>
            <div className="lv2-pain-list">
              <div className="lv2-pain-item">
                <div className="lv2-pain-emoji">🔁</div>
                <p>You Google-translated "תור" three times this week. You'll Google it again on Sunday — because Google Translate doesn't save anything.</p>
              </div>
              <div className="lv2-pain-item">
                <div className="lv2-pain-emoji">📓</div>
                <p>Your notebook has 400 words in it. You can't find anything. You stopped opening it two weeks ago.</p>
              </div>
              <div className="lv2-pain-item">
                <div className="lv2-pain-emoji">😤</div>
                <p>You're putting in the hours. You're showing up every day. But the words won't <em>stick.</em></p>
              </div>
            </div>
            <p className="lv2-pain-resolve">Shoresh is your personal word bank that saves everything you look up, organizes it, and drills you before you forget.</p>
          </div>
        </FadeIn>
      </div>

      {/* COMPARISON */}
      <div className="lv2-anti-section">
        <FadeIn>
          <div className="lv2-anti-inner">
            <div className="lv2-section-label" style={{ textAlign: "center" }}>WHY NOT JUST USE GOOGLE TRANSLATE?</div>
            <div className="lv2-section-title" style={{ textAlign: "center" }}>Google Translate tells you words. Shoresh makes them stick.</div>
            <p className="lv2-section-sub" style={{ textAlign: "center", margin: "0 auto 40px" }}>
              Every new oleh starts with Google Translate. Here's why it's not enough — and what you actually need.
            </p>
            <div className="lv2-compare-table">
              <div className="lv2-compare-col lv2-them">
                <h3>Google Translate</h3>
                <p className="lv2-compare-tagline">A lookup tool, not a learning tool</p>
                <ul>
                  <li><span className="lv2-dot-red">🔴</span> Gives you the word right now — gone the moment you close the tab</li>
                  <li><span className="lv2-dot-red">🔴</span> No grammar. No context. No binyan. Just a definition.</li>
                  <li><span className="lv2-dot-red">🔴</span> You look up the same word 4 times because nothing saves</li>
                </ul>
              </div>
              <div className="lv2-compare-col lv2-us">
                <h3>Shoresh</h3>
                <p className="lv2-compare-tagline">Built for the real Hebrew hitting you every day</p>
                <ul>
                  <li><span className="lv2-dot-green">🟢</span> Save words the moment you hear them — in class, at the bank, on the bus</li>
                  <li><span className="lv2-dot-green">🟢</span> Every word gets grammar: binyan, conjugation, gender, root</li>
                  <li><span className="lv2-dot-green">🟢</span> Spaced repetition brings back words before you forget them</li>
                </ul>
              </div>
            </div>
            <div className="lv2-compare-bottom">
              <p>Google Translate is where Hebrew goes to be forgotten. Shoresh is where it goes to <em>stick.</em></p>
            </div>
          </div>
        </FadeIn>
      </div>

      {/* HOW IT WORKS */}
      <section className="lv2-how-it-works">
        <FadeIn>
          <div className="lv2-section-label">HOW IT WORKS</div>
          <div className="lv2-section-title">Use it between ulpan classes. Five minutes changes everything.</div>
          <p className="lv2-section-sub">
            You're already doing the hard part — showing up every day. Shoresh makes sure the words stick after you leave.
          </p>
          <div className="lv2-steps">
            <div className="lv2-step">
              <div className="lv2-step-num">01</div>
              <h3>Tell Shoresh about your life</h3>
              <p>It builds your starter vocab around what matters to you.</p>
            </div>
            <div className="lv2-step">
              <div className="lv2-step-num">02</div>
              <h3>Build your word bank daily</h3>
              <p>One tap saves any word with its grammar and binyan.</p>
            </div>
            <div className="lv2-step">
              <div className="lv2-step-num">03</div>
              <h3>Review before your next class</h3>
              <p>Spaced repetition surfaces words right before you forget them.</p>
            </div>
          </div>
        </FadeIn>
      </section>

      {/* STUDY SETS */}
      <div className="lv2-study-section">
        <FadeIn>
          <div className="lv2-study-inner">
            <div className="lv2-study-copy">
              <div className="lv2-section-label">AI STUDY SETS</div>
              <div className="lv2-section-title">Doctor's appointment on Thursday? Prep the words now.</div>
              <p className="lv2-section-sub">
                Flashcards review what you've saved. Study sets go further — tell Shoresh what's coming up in your life, and it generates a ready-to-review set of exactly the Hebrew you need.
              </p>
            </div>
            <div className="lv2-study-phones">
              <div className="lv2-study-phone-wrap">
                <div className="lv2-phone-frame" style={{ width: 200 }}>
                  <img src="/landing/study-set-input.png" alt="Describe what you need" />
                </div>
                <div className="lv2-study-phone-label">1. DESCRIBE WHAT YOU NEED</div>
              </div>
              <div className="lv2-study-phone-arrow">→</div>
              <div className="lv2-study-phone-wrap">
                <div className="lv2-phone-frame lv2-phone-pop" style={{ width: 200 }}>
                  <img src="/landing/study-set-results.png" alt="Review and add to your set" />
                </div>
                <div className="lv2-study-phone-label">2. REVIEW & ADD TO YOUR SET</div>
              </div>
            </div>
          </div>
        </FadeIn>
      </div>

      {/* PRICING */}
      <div className="lv2-pricing-section" id="pricing">
        <FadeIn>
          <div className="lv2-pricing-inner">
            <div className="lv2-section-label" style={{ textAlign: "center" }}>EARLY ACCESS — FIRST 50 OLIM</div>
            <div className="lv2-section-title" style={{ textAlign: "center" }}>We're looking for 50 olim to try this first.</div>
            <p className="lv2-section-sub" style={{ textAlign: "center", margin: "0 auto 48px" }}>
              Shoresh is brand new — built by Sam Goldberg, a recent oleh who just went through ulpan. Early users get the tool free and help shape what gets built next.
            </p>

            <div className="lv2-early-strip">
              <div className="lv2-es-card"><div className="lv2-es-icon">🎁</div><h4>Free plan included</h4><p>Word bank, flashcards, Google Translate, grammar. Not a trial.</p></div>
              <div className="lv2-es-card"><div className="lv2-es-icon">🗣️</div><h4>You shape the product</h4><p>Tell Sam directly what's missing. It gets built around your feedback.</p></div>
              <div className="lv2-es-card"><div className="lv2-es-icon">🔒</div><h4>Early adopter pricing</h4><p>Lock in a lower Pro price before it goes up.</p></div>
            </div>

            <div className="lv2-pricing-grid">
              <div className="lv2-price-card">
                <h3>Free</h3>
                <div className="lv2-price">$0</div>
                <p className="lv2-price-note">The complete toolkit. No expiration.</p>
                <ul>
                  <li><span className="lv2-ck">✓</span> Google Translate</li>
                  <li><span className="lv2-ck">✓</span> Personal word bank</li>
                  <li><span className="lv2-ck">✓</span> Spaced repetition flashcards</li>
                  <li><span className="lv2-ck">✓</span> Study sets</li>
                  <li><span className="lv2-ck">✓</span> Manual categories</li>
                  <li><span className="lv2-ck">✓</span> 10 grammar breakdowns / month</li>
                  <li className="lv2-feat-off"><span className="lv2-cx">✗</span> AI translation</li>
                  <li className="lv2-feat-off"><span className="lv2-cx">✗</span> AI vocabulary generation</li>
                  <li className="lv2-feat-off"><span className="lv2-cx">✗</span> Smart auto-categorization</li>
                </ul>
                <button className="lv2-ccta lv2-ccta-sec" onClick={handleGetStarted}>Get Started Free</button>
              </div>
              <div className="lv2-price-card lv2-price-featured">
                <div className="lv2-pbadge">FULL POWER</div>
                <h3>Pro</h3>
                <div className="lv2-price">${PRICING.currentPrice} <span className="lv2-price-per">/ month</span></div>
                <p className="lv2-price-note">Everything in Free + AI that accelerates your Hebrew.</p>
                <ul>
                  <li><span className="lv2-ck">✓</span> <strong>Everything in Free</strong></li>
                  <li><span className="lv2-ck">✓</span> Unlimited grammar breakdowns</li>
                  <li><span className="lv2-ck">✓</span> AI vocabulary generation</li>
                  <li><span className="lv2-ck">✓</span> Smart auto-categorization</li>
                  <li><span className="lv2-ck">✓</span> AI-powered translation</li>
                  <li><span className="lv2-ck">✓</span> Cancel anytime</li>
                </ul>
                <button className="lv2-ccta lv2-ccta-pri" onClick={handleStartPro}>
                  Start Pro — ${PRICING.currentPrice}/mo
                </button>
              </div>
            </div>
          </div>
        </FadeIn>
      </div>

      {/* FAQ */}
      <section className="lv2-faq-section">
        <FadeIn>
          <div className="lv2-section-label" style={{ textAlign: "center" }}>FAQ</div>
          <div className="lv2-section-title" style={{ textAlign: "center" }}>Questions you probably have.</div>
          <div className="lv2-faq-list">
            <FaqItem
              question="I already use Google Translate. Why do I need this?"
              answer="Google Translate is a dictionary with a better UI. It gives you a word, you close the tab, and it's gone. Shoresh saves every word you look up, adds the grammar behind it, and drills you on it before you forget it. Google Translate is where Hebrew goes to be forgotten. Shoresh is where it goes to stick."
            />
            <FaqItem
              question="How is this different from a Google Doc or notebook?"
              answer="Your Google Doc doesn't conjugate verbs, show you the binyan, or quiz you on words slipping from memory. After 200 entries, good luck finding anything. Shoresh gives you grammar breakdowns, spaced repetition, search, category filtering, and AI study sets. It's what your notebook wishes it could be."
            />
            <FaqItem
              question="What do I get with Pro?"
              answer="AI-powered translations that actually understand Hebrew grammar, unlimited grammar breakdowns, auto-categorization, and AI study sets built around whatever you're doing that week. One ulpan lesson costs $80. Pro costs $30 a month and works every single day."
            />
            <FaqItem
              question={'"Designed to be deleted" — really?'}
              answer="Really. Shoresh is built for the hardest phase — the first year or two when you're drowning in new words. As your Hebrew gets stronger, you'll need it less. You'll stop looking words up because you know them. That's the whole point."
            />
          </div>
        </FadeIn>
      </section>

      {/* FOUNDER */}
      <section className="lv2-founder">
        <FadeIn>
          <div className="lv2-founder-inner">
            <div className="lv2-qm">&ldquo;</div>
            <blockquote>
              I made aliyah in 2025 and started ulpan, and within two weeks I realized I was Googling the same words over and over. My notebook was chaos. Duolingo had me matching pictures of cats while my landlord was texting me things I couldn't read. I needed something that would hold onto the words hitting me every day — and remind me before I forgot them. So I built it.
            </blockquote>
            <img src="/landing/sam-avatar.jpg" alt="Sam Goldberg" className="lv2-founder-av" />
            <div className="lv2-fn">Sam Goldberg</div>
            <div className="lv2-fr">Founder, Shoresh &middot; Made aliyah 2025</div>
          </div>
        </FadeIn>
      </section>

      {/* FINAL CTA */}
      <div className="lv2-final-cta">
        <div className="lv2-section-title" style={{ color: "white", maxWidth: 620, margin: "0 auto 16px" }}>
          Ulpan is temporary. The Hebrew you keep is permanent.
        </div>
        <p className="lv2-section-sub" style={{ color: "rgba(255,255,255,0.6)", margin: "0 auto 40px", maxWidth: 540, textAlign: "center" }}>
          You're already putting in the hours. Shoresh makes sure you keep what you earn. Free, 30 seconds, ready before tomorrow's class.
        </p>
        <button className="lv2-cta-btn" onClick={handleGetStarted}>
          Start My Word Bank — Free
        </button>
        <p className="lv2-cta-micro" style={{ color: "rgba(255,255,255,0.45)" }}>
          No credit card. No commitment. Built by a new oleh, for new olim.
        </p>
      </div>

      {/* FOOTER */}
      <footer className="lv2-footer">
        &copy; 2026 Shoresh. Built in Israel, for the people learning to live here.
      </footer>
    </div>
  );
}

/* ---- Sub-components ---- */

function FadeIn({ children }: { children: React.ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} className={`lv2-fade-in ${visible ? "lv2-visible" : ""}`}>
      {children}
    </div>
  );
}

function FaqItem({ question, answer }: { question: string; answer: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`lv2-faq-item ${open ? "lv2-faq-open" : ""}`}>
      <button className="lv2-faq-q" onClick={() => setOpen(!open)}>
        {question}
        <span className="lv2-faq-arrow">+</span>
      </button>
      <div className="lv2-faq-a">
        <p>{answer}</p>
      </div>
    </div>
  );
}
